import type { ReactNode } from "react";

/** 数と単位 (18ヶ月、12 項目、7-7、4.8 など。LPIC-1 のような英字とハイフンと数の塊も)。ここで折ると「18 / ヶ月」のように割れる */
const ONE_UNIT = "\\d+(?:\\.\\d+)?(?:\\s?[〜~～]\\s?\\d+(?:\\.\\d+)?)?\\s?(?:ヶ月|ヵ月|か月|カ月|ケ月|項目|時間|万字|万円|章|節|日|月|年|週|分|人|個|回|つ|本|秒|問|件|枚|行(?![動為政く])|字(?![幕体])|冊|円|進数|%|XP|KB|MB|GB)";
/** 「問 1〜5」「問 3」。単位が数の前に付く形も、数の範囲ごと 1 つの塊にする */
const PREFIX_ONE = "問\\s?\\d+(?:\\s?[〜~～]\\s?\\d+)?";
/** 「1 日 1 個」「2026 年 10 月 16 日」のように数と単位が続くときは、続きもまとめて 1 つの塊にする */
// 「所要 5 分」「約 5 分」「全 23 章」「第 4 章」の頭の語も数と離さない (「所要 / 5 分)」「第 / 4 章」のように割れる)
export const NUMBER_UNIT = new RegExp(`((?:(?:所要|約|第|全)\\s?)?${ONE_UNIT}(?:\\s?${ONE_UNIT})*|${PREFIX_ONE}|\\d+-\\d+|[A-Za-z]+-\\d+)`);

/** 文字列の中の数と単位を折らない塊にする (.lg-nowrap)。ほかの字はそのまま */
export function keepUnits(text: string): ReactNode {
  const parts = text.split(NUMBER_UNIT);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="lg-nowrap">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/** 「!!」「！！」。2 字を 1 つの塊にして字間 0 で見せる (.lg-bangs)。字間が空いて「! !」に見えるのを避ける */
export const BANGS = /([!！]{2,})/;

/**
 * 文字列の中の「!!」を 1 つの塊 (.lg-bangs) にする。ほかの字はそのまま。
 * 全角の「！！」は 1 字ずつが 1 字分の幅を取り、左に寄った印が離れて「! !」に見えるので、塊の中だけ半角の「!!」で描く
 */
export function keepBangs(text: string): ReactNode {
  const parts = text.split(BANGS);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="lg-bangs">
        {part.replace(/！/g, "!")}
      </span>
    ) : (
      part
    ),
  );
}

/** ハッシュタグ (#ゆめさくエンジニア)。タグの途中で折ると別の語に見える */
export const HASHTAG = /([#＃][^\s#＃、。，．,.!！?？()（）「」『』]+)/;

/** 投稿の字の中のハッシュタグを折らない塊にし、「!!」も塊にする。ほかの字はそのまま */
export function keepHashtags(text: string): ReactNode {
  const parts = text.split(HASHTAG);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="lg-nowrap">
        {part}
      </span>
    ) : (
      <span key={i}>{keepBangs(part)}</span>
    ),
  );
}

/**
 * 作品や教科書の名前に出てくる長いカタカナ語。ここで折ると「インシデ / ント」「プレ / ゼンテーション」のように語が割れる。
 * 長い方を先に書く (スミレクラウドをクラウドより先に当てる)。
 * プログラミング、スクール、カオス、マップは、「プログラミングスクールカオスマップに」(/news の題) のように続けて 1 語に見える所で、語の境目にだけ折れ目を置くために入れる
 * クロスサイト、スクリプティング、リクエスト、フォージェリ、コンテキストも同じ。「クロスサイトリクエストフォージェリ」(/security の帯の題) と
 * 「コンテキストエンジニアリング」(/glossary の帯の題) が狭い列で 1 語のままはみ出した
 */
export const KEEP_WORDS = /(コンテキスト|クロスサイト|スクリプティング|リクエスト|フォージェリ|スミレクラウド|モノクラフト|プレゼンテーション|セキュリティ|インシデント|プロジェクト|マネジメント|ダッシュボード|フルスタック|エンジニアリング|アプリケーション|ポートフォリオ|アウトプット|データベース|コミュニティ|コンピューター?|サイエンス|クラウド|プログラミング|スクール|カオス|マップ(?!ピング))/;

/** 行頭に置けない字 (行頭禁則)。句読点、閉じ括弧、長音、小書きの仮名、感嘆符と疑問符 */
const NO_LINE_START = /^[、。，．,.)）\]］}｝」』】〕〉》"'”’ーぁぃぅぇぉっゃゅょゎァィゥェォッャュョヮヵヶ!！?？:：;；・]/;

/** 語の後ろに折れ目を置くか。後ろに字が無いか、行頭に置けない字が続くなら置かない */
function breakAfter(rest: string): boolean {
  return rest !== "" && !NO_LINE_START.test(rest);
}

/**
 * 長いカタカナ語を折らない塊にし、語と語の間にだけ折れ目 (wbr) を置く。数と単位は keepUnits と同じに守る。
 * 表の行のように狭い欄に長い作品の名前を出すときに使う
 */
export function keepWords(text: string): ReactNode {
  const parts = text.split(KEEP_WORDS);
  if (parts.length === 1) return keepUnits(text);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i}>
        <span className="lg-nowrap">{part}</span>
        {/* 後ろが行頭に置けない字 (句読点、閉じ括弧、長音、小書きの仮名) なら折れ目を置かない */}
        {breakAfter(parts.slice(i + 1).join("")) ? <wbr /> : null}
      </span>
    ) : (
      <span key={i}>{keepUnits(part)}</span>
    ),
  );
}

/** 語の後ろに付く助詞。カタカナ語や漢語の直後のこれらの前は、語の切れ目として折ってよい */
const PARTICLE_START = /^[のをにがとでへはも]/;

/**
 * 最後の 4 字を折らない塊にし、前は keepWords と同じに守る。目次の項目のような短い行で、
 * 最後の行が 1〜2 字だけになる (「全体 / 像」) のを避ける。本文の rehypeKeepTail と同じ考え。
 * 塊が長いカタカナ語の途中から始まるときは語の頭まで広げ、塊が助詞で始まるときだけ塊の前に折れ目を置く
 * (「このプロジェクト / の全体像」。折れ目が無いと「この / プロジェクトの全体像」になる)
 */
export function keepTail(text: string, tail = 4): ReactNode {
  const chars = Array.from(text);
  if (chars.length <= tail + 2) return <span className="lg-nowrap">{text}</span>;
  let cut = chars.length - tail;
  for (const m of text.matchAll(new RegExp(KEEP_WORDS.source, "g"))) {
    const from = Array.from(text.slice(0, m.index)).length;
    const to = from + Array.from(m[0]).length;
    if (from < cut && cut < to) cut = from;
  }
  if (cut === 0) return <span className="lg-nowrap">{text}</span>;
  const head = chars.slice(0, cut).join("");
  const rest = chars.slice(cut).join("");
  const breakBefore = /[^\u3040-\u309f]$/.test(head) && PARTICLE_START.test(rest);
  return (
    <>
      {keepWords(head)}
      {breakBefore ? <wbr /> : null}
      <span className="lg-nowrap">{rest}</span>
    </>
  );
}
